import type { SetupOption } from "./OnboardingSetupShopContent";

import React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import useInstallShopify from "@editor/hooks/useInstallShopify";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { onboardingUtils } from "@editor/trpc/onboarding";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { userUtils } from "@editor/trpc/user";
import { workspaceUtils } from "@editor/trpc/workspace";

import { useNavigate, useSearchParams } from "react-router-dom";
import { isNullish } from "replo-utils/lib/misc";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";
import OnboardingSetupShopContent from "./OnboardingSetupShopContent";

const OnboardingSetupShop: React.FC = () => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();

  const hasShopifyParams = React.useMemo(() => {
    const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");
    const pendingAppInstallationId = searchParams.get(
      "pendingAppInstallationId",
    );
    return Boolean(shopifyIntegrationId || pendingAppInstallationId);
  }, [searchParams]);

  const [selectedHostingType, setSelectedHostingType] =
    React.useState<SetupOption | null>(hasShopifyParams ? "import" : null);

  const [selectedProduct, setSelectedProduct] =
    React.useState<SetupOption | null>(null);

  const [shopName] = useLocalStorageState("onboarding-shopName", "", {
    schema: z.string(),
  });
  const [subdomain] = useLocalStorageState("onboarding-subdomain", "", {
    schema: z.string(),
  });
  const [productDescription] = useLocalStorageState(
    "onboarding-productDescription",
    null,
    {
      schema: z
        .enum([
          "shopify",
          "ecommerce",
          "software",
          "digital",
          "membership",
          "courses",
          "professional",
          "events",
          "retail",
        ])
        .nullable(),
    },
  );
  const [annualRevenue] = useLocalStorageState(
    "onboarding-annualRevenue",
    null,
    {
      schema: z
        .enum(["0-500k", "500k-2m", "2m-5m", "5m-10m", "10m-50m", "50m+"])
        .nullable(),
    },
  );

  const { mutateAsync: createProjectAsync, isPending: isCreatingProject } =
    projectRouter.create.useMutation();

  const workspaceId = useCurrentWorkspaceId();

  const installShopify = useInstallShopify();

  const mapRevenueToNumber = (revenue: string | null): number => {
    switch (revenue) {
      case "0-500k":
        return 0;
      case "500k-2m":
        return 500000;
      case "2m-5m":
        return 2000000;
      case "5m-10m":
        return 5000000;
      case "10m-50m":
        return 10000000;
      case "50m+":
        return 50000000;
      default:
        return 0;
    }
  };

  const handleSubmit = async (_e: React.MouseEvent) => {
    const isCompleteFormValid =
      !isNullish(annualRevenue) &&
      productDescription !== null &&
      shopName !== "" &&
      subdomain !== "" &&
      user &&
      workspaceId &&
      selectedHostingType &&
      selectedProduct;

    if (!isCompleteFormValid) {
      return;
    }

    const hostingType = selectedProduct === "ai" ? "replo" : "shopify";

    const result = await createProjectAsync({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: hostingType,
      shopDomainPrefix: subdomain.trim(),
      productDescription: productDescription,
      annualRevenue: mapRevenueToNumber(annualRevenue),
      workspaceName: `${shopName}'s Workspace`,
      domain: user.email.split("@")[1]!.toLowerCase(),
    });

    const shouldRedirectToShopifyAppStore = selectedHostingType === "import";

    await workspaceUtils.getUserWorkspacesList.invalidate();
    await projectUtils.listWithStats.invalidate();

    // Note (Noah, 2025-09-26): If we're about to redirect to the Shopify App Store,
    // DON'T invalidate the user.get query, because that would cause the page to redirect
    // to the project page the user just created since the user now has a default project
    // id (the one which was just created).
    if (!shouldRedirectToShopifyAppStore) {
      await userUtils.get.invalidate();
    }
    await onboardingUtils.checkSubdomainAvailability.invalidate();

    if (user && selectedHostingType) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "connect-store",
        stepType: "connect-store",
        stepData: {
          selectedHostingType,
          selectedProduct,
        },
      });
    }
    localStorage.removeItem("onboarding-shopName");
    localStorage.removeItem("onboarding-subdomain");
    localStorage.removeItem("onboarding-productDescription");
    localStorage.removeItem("onboarding-annualRevenue");

    if (shouldRedirectToShopifyAppStore) {
      installShopify({
        workspaceId: workspaceId ?? undefined,
        cookie: {
          type: "onboarding",
          workspaceId: result.project.ownerWorkspaceId,
          projectId: result.project.id,
        },
        projectHostingType: hostingType,
      });
    } else {
      navigate(`/shops/${result.project?.id}/home`);
    }
  };

  const handleBack = () => {
    navigate("/onboarding/shop-info");
  };

  const isSubmitDisabled =
    isCreatingProject || !selectedHostingType || !selectedProduct;

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-35 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">
            How do you sell today?
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            Replo works with Shopify, other platforms, or completely on its own.
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center w-full px-5 lg:px-0 pb-80">
          <div className="lg:w-[1100px] w-full">
            <OnboardingSetupShopContent
              selectedHostingType={selectedHostingType}
              setSelectedHostingType={(option) =>
                setSelectedHostingType(option)
              }
              selectedProduct={selectedProduct}
              setSelectedProduct={(option) => setSelectedProduct(option)}
              isShopContext={false}
            />
          </div>
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={3}
          onNext={handleSubmit}
          onBack={handleBack}
          className="w-full lg:max-w-[1100px] px-5 lg:px-0"
          isNextLoading={isCreatingProject}
          isNextDisabled={isSubmitDisabled}
          totalSteps={3}
        />
      </div>
    </div>
  );
};

export default OnboardingSetupShop;
